import { AlertTriangle, Clock3, Loader2, RefreshCw, ServerCog } from 'lucide-react';

import type { AnalysisQueueStats } from '../api/admin';
import AdminShell from '../components/AdminShell';
import { useAdminQueues } from '../hooks/useAdminQueues';

interface QueueSectionProps {
  title: string;
  stats: AnalysisQueueStats;
}

function QueueSection({ title, stats }: QueueSectionProps) {
  return (
    <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-100">{title}</h2>
          <p className="mt-1 text-xs text-zinc-500">
            Provider: <span className="text-zinc-300">{stats.provider}</span>
            {' · '}
            Mode: <span className="text-zinc-300">{stats.mode}</span>
            {stats.fallbackProvider ? (
              <>
                {' · '}
                Fallback: <span className="text-amber-400">{stats.fallbackProvider}</span>
              </>
            ) : null}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-lg border border-zinc-700 bg-zinc-950 px-3 py-1.5 text-xs text-zinc-300">
          <ServerCog className="h-3.5 w-3.5 text-amber-500" />
          <span>Total waiting: {stats.totals.waiting}</span>
          <span className="text-zinc-600">|</span>
          <span>Active: {stats.totals.active}</span>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-4">
        {([
          { key: 'paid', label: 'Paid lane', tone: 'emerald' },
          { key: 'free', label: 'Free lane', tone: 'amber' },
        ] as const).map(({ key, label, tone }) => {
          const lane = stats[key];
          const borderTone = tone === 'emerald' ? 'border-emerald-900/30' : 'border-amber-900/30';
          const bgTone = tone === 'emerald' ? 'bg-emerald-950/10' : 'bg-amber-950/10';
          const textTone = tone === 'emerald' ? 'text-emerald-400' : 'text-amber-400';
          return (
            <div key={key} className={`rounded-xl border ${borderTone} ${bgTone} p-4`}>
              <div className="flex items-center justify-between">
                <p className={`text-sm font-semibold ${textTone}`}>{label}</p>
                <span className="text-[11px] text-zinc-500">
                  Concurrency cap: {key === 'paid' ? stats.limits.paidConcurrency : stats.limits.freeConcurrency}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2">
                  <p className="text-zinc-500">Waiting</p>
                  <p className="mt-1 text-zinc-100 font-semibold">{lane.waiting}</p>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2">
                  <p className="text-zinc-500">Active</p>
                  <p className="mt-1 text-zinc-100 font-semibold">{lane.active}</p>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2">
                  <p className="text-zinc-500">Delayed</p>
                  <p className="mt-1 text-zinc-100 font-semibold">{lane.delayed}</p>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2">
                  <p className="text-zinc-500">Failed</p>
                  <p className="mt-1 text-zinc-100 font-semibold">{lane.failed ?? '-'}</p>
                </div>
                <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-2">
                  <p className="text-zinc-500">Completed</p>
                  <p className="mt-1 text-zinc-100 font-semibold">{lane.completed ?? '-'}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
        <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-950 px-2 py-1 text-zinc-300">
          <Clock3 className="h-3 w-3 text-zinc-500" />
          Total concurrency: {stats.limits.totalConcurrency}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-950 px-2 py-1 text-zinc-300">
          Paid burst: {stats.limits.paidBurst}
        </span>
        {typeof stats.totals.failed === 'number' && stats.totals.failed > 0 ? (
          <span className="inline-flex items-center gap-1 rounded-full border border-red-900/40 bg-red-950/20 px-2 py-1 text-red-300">
            <AlertTriangle className="h-3 w-3" />
            Failed jobs: {stats.totals.failed}
          </span>
        ) : null}
      </div>
    </section>
  );
}

export default function QueuesPage() {
  const { queues, loading, refresh } = useAdminQueues();

  return (
    <AdminShell
      title="Queues"
      description="Monitor real-time queue health for analysis, script generation, and channel analytics workloads."
      actions={(
        <button
          onClick={() => refresh().catch(() => undefined)}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:text-zinc-100 transition-colors text-xs"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      )}
    >
      {loading && !queues ? (
        <div className="flex items-center gap-2 text-zinc-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading queue stats…
        </div>
      ) : null}

      {queues ? (
        <div className="space-y-6">
          <QueueSection title="Analysis Queue" stats={queues.analysis} />
          <QueueSection title="Script Generation Queue" stats={queues.scripts} />
          <QueueSection title="Channel Analysis Queue" stats={queues.channelAnalytics} />
        </div>
      ) : null}
    </AdminShell>
  );
}
